import { areIdentical } from './utils';

type ClientSearchItem = {
    id?: string | null;
    name?: string | null;
    clientFileNumber?: number | null;
};

const CLIENT_OPTION_PREFIX = 'client-option:';

const getClientOptionValue = (clientId: string): string => `${CLIENT_OPTION_PREFIX}${clientId}`;

export type ClientSearchConfig<T extends ClientSearchItem> = {
    options: string[];
    optionLabelResolver: (option: string) => string;
    searchTextResolver: (option: string) => string;
    searchMatcher: (inputValue: string, option: string) => boolean;
    getClientByOption: (option: string | null | undefined) => T | undefined;
    getOptionByClientId: (clientId: string | null | undefined) => string | undefined;
};

export const getClientSearchOptions = <T extends ClientSearchItem>(clients: T[]): string[] =>
    [...new Set(
        clients
            .map(client => client.name?.trim())
            .filter((name): name is string => !!name?.length),
    )];

export const buildClientSearchTextLookup = <T extends ClientSearchItem>(clients: T[]): Record<string, string> => {
    const searchTokensByName = new Map<string, Set<string>>();

    clients.forEach(client => {
        const name = client.name?.trim();
        if (!name) {
            return;
        }

        if (!searchTokensByName.has(name)) {
            searchTokensByName.set(name, new Set([name]));
        }

        if (client.clientFileNumber != null) {
            searchTokensByName.get(name)?.add(client.clientFileNumber.toString());
        }
    });

    return Object.fromEntries(
        Array.from(searchTokensByName.entries()).map(([name, tokens]) => [name, Array.from(tokens).join(' ')]),
    );
};

export const buildClientFileNumbersLookup = <T extends ClientSearchItem>(clients: T[]): Record<string, string[]> => {
    const fileNumbersByName = new Map<string, Set<string>>();

    clients.forEach(client => {
        const name = client.name?.trim();
        if (!name) {
            return;
        }

        if (!fileNumbersByName.has(name)) {
            fileNumbersByName.set(name, new Set());
        }

        if (client.clientFileNumber != null) {
            fileNumbersByName.get(name)?.add(client.clientFileNumber.toString());
        }
    });

    return Object.fromEntries(
        Array.from(fileNumbersByName.entries()).map(([name, fileNumbers]) => [name, Array.from(fileNumbers)]),
    );
};

export const createClientSearchTextResolver =
    (searchTextLookup: Record<string, string>) =>
    (option: string): string =>
        searchTextLookup[option] ?? option;

export const createClientSearchMatcher =
    (
        searchTextLookup: Record<string, string>,
        clientFileNumbersLookup: Record<string, string[]>,
    ) =>
    (inputValue: string, option: string): boolean => {
        const trimmedInputValue = inputValue.trim();

        if (!trimmedInputValue.length) {
            return true;
        }

        if (/^\d+$/.test(trimmedInputValue)) {
            return clientFileNumbersLookup[option]?.includes(trimmedInputValue) ?? false;
        }

        return areIdentical(trimmedInputValue, searchTextLookup[option] ?? option);
    };

export const createClientSearchConfig = <T extends ClientSearchItem>(
    clients: T[],
    extraOptions: string[] = [],
) : ClientSearchConfig<T> => {
    const clientsByOption = new Map<string, T>();
    const optionByClientId = new Map<string, string>();
    const searchTextLookup: Record<string, string> = Object.fromEntries(extraOptions.map(option => [option, option]));
    const clientFileNumbersLookup: Record<string, string[]> = Object.fromEntries(extraOptions.map(option => [option, [] as string[]]));
    const nameCounts = new Map<string, number>();

    clients.forEach(client => {
        const clientId = client.id?.trim();
        const name = client.name?.trim();
        if (!clientId || !name) {
            return;
        }

        const option = getClientOptionValue(clientId);
        clientsByOption.set(option, client);
        optionByClientId.set(clientId, option);
        searchTextLookup[option] = [name, client.clientFileNumber?.toString()].filter(value => !!value?.length).join(' ');
        clientFileNumbersLookup[option] = client.clientFileNumber != null ? [client.clientFileNumber.toString()] : [];
        nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    });

    const options = [...extraOptions, ...Array.from(clientsByOption.keys())];
    const optionLabelResolver = (option: string): string => {
        if (extraOptions.includes(option)) {
            return option;
        }

        const client = clientsByOption.get(option);
        const name = client?.name?.trim();
        if (!name) {
            return option;
        }

        if ((nameCounts.get(name) ?? 0) > 1 && client?.clientFileNumber != null) {
            return `${name} - ${client.clientFileNumber}`;
        }

        return name;
    };

    return {
        options,
        optionLabelResolver,
        searchTextResolver: createClientSearchTextResolver(searchTextLookup),
        searchMatcher: createClientSearchMatcher(searchTextLookup, clientFileNumbersLookup),
        getClientByOption: (option) => option ? clientsByOption.get(option) : undefined,
        getOptionByClientId: (clientId) => clientId ? optionByClientId.get(clientId) : undefined,
    };
};

export const getClientFileNumberLabel = (clientFileNumber: number | null | undefined, label: string): string | undefined =>
    clientFileNumber == null ? undefined : `${label}: ${clientFileNumber}`;
